import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import api, { getCompanies } from '../../utils/api';
import { ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import BlogEditor from '../blog-submit/BlogEditor';

const CreatePost = () => {
    const navigate = useNavigate();

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [tagInput, setTagInput] = useState('');
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('Interview Experience');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [previewMode, setPreviewMode] = useState(false);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const editId = queryParams.get('edit');

    const [companies, setCompanies] = useState([]);
    const categories = ["Interview Experience", "Question Bank", "Career Advice"];

    React.useEffect(() => {
        const checkAuth = async () => {
            try {
                const res = await api.get('/auth/profile');
                setCurrentUser(res.data);
            } catch (err) {
                setCurrentUser(null);
            } finally {
                setCheckingAuth(false);
            }
        };
        const fetchCompanies = async () => {
            try {
                const data = await getCompanies();
                setCompanies(data);
            } catch (err) {
                console.error("Failed to fetch companies:", err);
            }
        };
        const fetchPostForEdit = async () => {
            if (!editId) return;
            try {
                const res = await api.get(`/api/forum/posts/${editId}`);
                setTitle(res.data.title);
                setContent(res.data.content);
                setTagInput(res.data.tags?.join(', ') || '');
                setSelectedCategory(res.data.category || 'Interview Experience');
                // Find company tag if exists
                const companyTag = res.data.tags?.find(t => companies.some(c => c.name.toLowerCase() === t.toLowerCase()));
                if (companyTag) setSelectedCompany(companies.find(c => c.name.toLowerCase() === companyTag.toLowerCase())?.name || '');
            } catch (error) {
                toast.error("Failed to load post for editing.");
            }
        };

        const init = async () => {
            await checkAuth();
            await fetchCompanies();
            if (editId) fetchPostForEdit();
        };
        init();
    }, [editId, companies.length]);

    const extractHashtags = (text) => {
        if (!text) return [];
        const regex = /#(\w+)/g;
        const matches = text.match(regex);
        return matches ? matches.map(tag => tag.substring(1).toLowerCase()) : [];
    };

    const handleSubmit = async (e, shouldPublish = true) => {
        if (e) e.preventDefault();

        if (!title.trim() || !content.trim()) {
            toast.error("Title and content are required.");
            return;
        }

        const tagsFromInput = tagInput.split(',').map(t => t.trim().replace('#', '')).filter(t => t);
        const tagsFromContent = extractHashtags(content);
        const tagsFromTitle = extractHashtags(title);

        const allTags = [...new Set([...tagsFromInput, ...tagsFromContent, ...tagsFromTitle])];
        if (selectedCompany) allTags.push(selectedCompany.toLowerCase());

        setIsSubmitting(true);
        try {
            let res;
            const payload = {
                title,
                content,
                tags: allTags,
                category: selectedCategory,
                is_published: shouldPublish
            };

            if (editId) {
                res = await api.put(`/api/forum/posts/${editId}`, payload);
                toast.success(shouldPublish ? "Experience updated and published!" : "Draft updated!");
            } else {
                res = await api.post('/api/forum/posts', payload);
                toast.success(shouldPublish ? "Experience published successfully!" : "Draft saved!");
            }
            navigate(shouldPublish ? `/community/post/${res.data.id || editId}` : '/community');
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data?.error || error.message || "Failed to create post.";
            toast.error(errorMsg);
            setIsSubmitting(false);
        }
    };

    if (checkingAuth) {
        return (
            <div className="min-h-screen bg-white flex flex-col font-sans">
                <NavigationHeader />
                <div className="flex-1 flex items-center justify-center">
                    <Icon name="Loader2" className="animate-spin text-emerald-600" size={32} />
                </div>
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
                <NavigationHeader />
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                        <Icon name="Lock" size={24} className="text-emerald-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Login Required</h2>
                    <p className="text-slate-500 mb-6 max-w-xs">You need to be logged in to share your journey with the community.</p>
                    <Link to="/login" className="px-8 py-3 bg-[#059669] text-white font-bold rounded-xl hover:bg-[#047857] transition shadow-lg shadow-emerald-500/20">
                        Go to Login
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <NavigationHeader />

            <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 pt-24 pb-32">
                {/* Breadcrumbs */}
                <div className="flex items-center gap-2 text-[10px] font-blacktracking-[0.2em] text-slate-400 mb-8">
                    <Link to="/community" className="hover:text-emerald-600 transition-colors">Community</Link>
                    <Icon name="ChevronRight" size={12} strokeWidth={3} />
                    <span className="text-slate-900">{editId ? 'Edit Post' : 'Create New Post'}</span>
                </div>

                <div className="space-y-10">
                    {/* Header Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-[1fr,240px] gap-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-blacktracking-[0.2em] text-slate-400 ml-1">Post Title</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. My Google L4 SRE Interview Experience"
                                className="w-full px-0 py-2 border-b-2 border-slate-100 focus:border-emerald-500 text-2xl font-bold text-slate-900 outline-none transition-all placeholder:text-slate-200"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-blacktracking-[0.2em] text-slate-400 ml-1">Company Tag</label>
                            <div className="relative group">
                                <select
                                    value={selectedCompany}
                                    onChange={(e) => setSelectedCompany(e.target.value)}
                                    className="w-full pl-4 pr-10 py-3 bg-[#F8FAFC] border-none rounded-xl text-xs font-bold text-slate-700 appearance-none outline-none group-hover:bg-[#F1F5F9] transition-all cursor-pointer"
                                >
                                    <option value="">Select Company</option>
                                    {companies.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                                </select>
                                <Icon name="ChevronDown" size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none group-hover:text-slate-600" />
                            </div>
                        </div>
                    </div>

                    {/* Editor Area */}
                    <div className="space-y-4">
                        <BlogEditor 
                            content={content} 
                            onChange={setContent} 
                            placeholder="Describe your experience, the rounds, and the questions asked..." 
                        />
                    </div>

                    {/* Bottom Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-10 border-t border-slate-100">
                        <div className="space-y-4">
                            <label className="text-[10px] font-blacktracking-[0.2em] text-slate-400">Category</label>
                            <div className="flex flex-wrap gap-2">
                                {categories.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => setSelectedCategory(cat)}
                                        className={`px-4 py-2 rounded-xl text-xs font-bold border-2 transition-all ${selectedCategory === cat
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-100 shadow-sm'
                                                : 'bg-white text-slate-400 border-slate-50 hover:border-slate-100'
                                            }`}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-4">
                            <label className="text-[10px] font-blacktracking-[0.2em] text-slate-400 ml-1">Skill Tags</label>
                            <div className="relative">
                                <Icon name="Hash" size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    placeholder="Java, System Design, DSA (comma separated)"
                                    className="w-full pl-10 pr-4 py-4 bg-[#F8FAFC] border-none rounded-2xl text-xs font-bold text-slate-700 outline-none transition-all placeholder:text-slate-300"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fixed Bottom Action Bar */}
                <div className="fixed bottom-0 left-0 right-0 h-24 bg-white/80 backdrop-blur-md border-t border-slate-100 z-50">
                    <div className="max-w-4xl mx-auto h-full px-6 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setPreviewMode(!previewMode)}
                                className={`flex items-center gap-2 text-xs font-bold transition-colors ${previewMode ? 'text-emerald-600' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                <div className={`w-8 h-4 rounded-full relative transition-colors ${previewMode ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                                    <div className={`absolute top-1 w-2 h-2 rounded-full bg-white transition-all ${previewMode ? 'left-5' : 'left-1'}`} />
                                </div>
                                Preview Mode
                            </button>
                            <div className="w-px h-6 bg-slate-100 mx-2" />
                            <button
                                onClick={() => navigate('/community')}
                                className="text-xs font-bold text-slate-400 hover:text-red-500 transition-colors"
                            >
                                Discard
                            </button>
                        </div>

                        <div className="flex items-center gap-3">
                            <button 
                                onClick={(e) => handleSubmit(e, false)}
                                disabled={isSubmitting || !title.trim()}
                                className="px-6 py-3 bg-white text-slate-600 text-xs font-bold rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-50"
                            >
                                {editId ? 'Update Draft' : 'Save Draft'}
                            </button>
                            <button
                                onClick={(e) => handleSubmit(e, true)}
                                disabled={isSubmitting || !title.trim() || !content.trim()}
                                className="px-8 py-3 bg-[#059669] text-white text-xs font-bold rounded-xl hover:bg-[#047857] transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:shadow-none"
                            >
                                {isSubmitting ? (editId ? 'Updating...' : 'Publishing...') : (editId ? 'Update & Publish' : 'Publish Experience')}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-12 p-6 bg-[#F8FAFC] rounded-2xl border border-slate-100">
                    <div className="flex gap-3">
                        <Icon name="Info" size={18} className="text-emerald-600 shrink-0" />
                        <div>
                            <h4 className="text-xs font-bold text-slate-900 mb-1">Posting Tip</h4>
                            <p className="text-[11px] text-slate-500 leading-relaxed font-medium">
                                Structure your interview experience by rounds. Be clear about the questions asked and the expectations, but avoid sharing sensitive company intellectual property.
                            </p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default CreatePost;
